import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend } from "recharts";
import { Download, FileText, Receipt, Banknote } from "lucide-react";
import HelpTip from "@/components/HelpTip";
import { SECTION_HELP, KPI_HELP } from "@/lib/helpTexts";

const COLORS = ["#0f172a", "#dc2626", "#10b981", "#f59e0b", "#3b82f6", "#8b5cf6", "#ec4899", "#14b8a6"];

export default function Reports() {
    const [data, setData] = useState(null);
    const [cashReport, setCashReport] = useState(null);
    const [months, setMonths] = useState(6);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/dashboard/executive");
                if (data) setData(data);
            } catch (e) { console.warn("reports fetch failed", e); }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/reports/cash-vs-invoiced?months=${months}`);
                if (data) setCashReport(data);
            } catch (e) { console.warn("cash report fetch failed", e); }
        })();
    }, [months]);

    const downloadCSV = () => {
        if (!cashReport?.months?.length) return;
        const rows = [
            ["Mes", "Servicios facturados", "Total facturado", "IVA recaudado", "Servicios en efectivo", "Total efectivo (sin IVA)", "% efectivo", "Total general"],
            ...cashReport.months.map(m => [
                m.label, m.invoiced_count, m.invoiced_total.toFixed(2), m.invoiced_iva.toFixed(2),
                m.cash_count, m.cash_total.toFixed(2), m.cash_pct.toFixed(2) + "%", m.total_revenue.toFixed(2),
            ]),
        ];
        const csv = rows.map(r => r.map(v => `"${v}"`).join(",")).join("\n");
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `FindMed-Facturado-vs-Efectivo-${months}m.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
    };

    if (!data) return <div className="text-sm text-slate-500 font-mono">Cargando reportes...</div>;
    const { kpis, top_clients, services_by_type, daily_revenue } = data;

    return (
        <div className="space-y-4" data-testid="reports-page">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="fm-card p-4"><div className="text-[10px] uppercase font-bold text-slate-500 tracking-widest">Total servicios</div><div className="font-mono font-black text-3xl tabular-nums">{kpis.services_total}</div></div>
                <div className="fm-card p-4"><div className="text-[10px] uppercase font-bold text-slate-500 tracking-widest">Ingresos mes</div><div className="font-mono font-black text-3xl tabular-nums">{money(kpis.revenue_month)}</div></div>
                <div className="fm-card p-4"><div className="text-[10px] uppercase font-bold text-slate-500 tracking-widest">Por cobrar</div><div className="font-mono font-black text-3xl tabular-nums text-amber-600">{money(kpis.accounts_receivable)}</div></div>
                <div className="fm-card p-4"><div className="text-[10px] uppercase font-bold text-slate-500 tracking-widest">Cancelados</div><div className="font-mono font-black text-3xl tabular-nums text-red-600">{kpis.services_canceled}</div></div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="fm-card p-5">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-display font-bold text-base">Ingresos · Tendencia 14 días</h3>
                        <button className="text-[10px] uppercase tracking-widest font-bold text-slate-600 hover:text-slate-900 flex items-center gap-1"><Download className="w-3 h-3" /> CSV</button>
                    </div>
                    <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={daily_revenue}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip formatter={(v) => money(v)} />
                            <Line type="monotone" dataKey="total" stroke="#dc2626" strokeWidth={2} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                <div className="fm-card p-5">
                    <h3 className="font-display font-bold text-base mb-3">Servicios por tipo</h3>
                    <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                            <Pie data={services_by_type} dataKey="count" nameKey="code" cx="50%" cy="50%" outerRadius={90} label>
                                {services_by_type.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                <div className="fm-card p-5 lg:col-span-2">
                    <h3 className="font-display font-bold text-base mb-3">Top clientes del mes</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={top_clients} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={180} />
                            <Tooltip formatter={(v) => money(v)} />
                            <Bar dataKey="total" fill="#0f172a" radius={0} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* ============ Reporte mensual: Facturado vs Efectivo ============ */}
            {cashReport && (
            <div className="fm-card p-5 border-l-4 border-l-amber-500" data-testid="cash-vs-invoiced-report">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                    <div>
                        <h3 className="font-display font-bold text-base flex items-center gap-2">
                            <Receipt className="w-4 h-4 text-amber-600" />
                            Ingresos facturados vs efectivo sin factura
                            <HelpTip text={SECTION_HELP.cash_vs_invoiced} />
                        </h3>
                        <div className="text-[11px] text-slate-500 font-mono">
                            Ventana: últimos {cashReport.summary.months_in_range} meses · {cashReport.summary.cash_pct}% del ingreso fue en efectivo sin CFDI
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <select value={months} onChange={(e) => setMonths(parseInt(e.target.value))} className="fm-input text-xs py-1.5" data-testid="cash-report-months">
                            <option value={3}>Últimos 3 meses</option>
                            <option value={6}>Últimos 6 meses</option>
                            <option value={12}>Últimos 12 meses</option>
                            <option value={24}>Últimos 24 meses</option>
                        </select>
                        <button onClick={downloadCSV} className="fm-btn-outline text-xs" data-testid="cash-report-csv-btn">
                            <Download className="w-3 h-3" /> CSV
                        </button>
                    </div>
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                    <div className="border border-slate-200 rounded-sm p-3 bg-white">
                        <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold text-slate-500 tracking-widest mb-1">
                            <FileText className="w-3 h-3" /> Facturado
                        </div>
                        <div className="font-mono font-black text-2xl tabular-nums text-slate-900">{money(cashReport.summary.invoiced_total)}</div>
                        <div className="text-[10px] text-slate-400 font-mono">{cashReport.summary.invoiced_count} servicios</div>
                    </div>
                    <div className="border border-slate-200 rounded-sm p-3 bg-white">
                        <div className="text-[10px] uppercase font-bold text-slate-500 tracking-widest mb-1">IVA recaudado</div>
                        <div className="font-mono font-black text-2xl tabular-nums text-emerald-700">{money(cashReport.summary.invoiced_iva)}</div>
                        <div className="text-[10px] text-slate-400 font-mono">16% sobre subtotal</div>
                    </div>
                    <div className="border border-amber-300 rounded-sm p-3 bg-amber-50/60">
                        <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold text-amber-800 tracking-widest mb-1">
                            <Banknote className="w-3 h-3" /> Efectivo sin CFDI
                        </div>
                        <div className="font-mono font-black text-2xl tabular-nums text-amber-700">{money(cashReport.summary.cash_total)}</div>
                        <div className="text-[10px] text-amber-700 font-mono">{cashReport.summary.cash_count} servicios · {cashReport.summary.cash_pct}%</div>
                    </div>
                    <div className="border border-slate-200 rounded-sm p-3 bg-white">
                        <div className="text-[10px] uppercase font-bold text-slate-500 tracking-widest mb-1">Total general</div>
                        <div className="font-mono font-black text-2xl tabular-nums">{money(cashReport.summary.total_revenue)}</div>
                        <div className="text-[10px] text-slate-400 font-mono">{cashReport.summary.invoiced_count + cashReport.summary.cash_count} servicios</div>
                    </div>
                </div>

                {/* Chart */}
                {cashReport.months.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={cashReport.months}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                            <Tooltip formatter={(v) => money(v)} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar dataKey="invoiced_total" name="Facturado" stackId="a" fill="#0f172a" />
                            <Bar dataKey="cash_total" name="Efectivo sin CFDI" stackId="a" fill="#f59e0b" />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="text-center py-10 text-sm text-slate-500 font-mono">
                        Aún no hay servicios cerrados para reportar.
                    </div>
                )}

                {/* Tabla mensual + Top clientes en efectivo */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-5">
                    <div>
                        <h4 className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Desglose mensual</h4>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="bg-slate-50 border-b border-slate-200">
                                        <th className="text-left py-2 px-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Mes</th>
                                        <th className="text-right py-2 px-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Facturado</th>
                                        <th className="text-right py-2 px-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Efectivo</th>
                                        <th className="text-right py-2 px-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">% Ef.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cashReport.months.slice().reverse().map((m) => (
                                        <tr key={m.month} className="border-b border-slate-100">
                                            <td className="py-1.5 px-2 font-mono">{m.label}</td>
                                            <td className="py-1.5 px-2 text-right font-mono tabular-nums">{money(m.invoiced_total)}</td>
                                            <td className="py-1.5 px-2 text-right font-mono tabular-nums text-amber-700">{money(m.cash_total)}</td>
                                            <td className="py-1.5 px-2 text-right font-mono tabular-nums">
                                                <span className={`fm-badge ${m.cash_pct > 30 ? "fm-badge-warning" : "fm-badge-neutral"}`}>
                                                    {m.cash_pct.toFixed(1)}%
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div>
                        <h4 className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Top clientes en efectivo (atención)</h4>
                        {cashReport.top_cash_clients.length === 0 ? (
                            <div className="text-xs text-slate-400 italic font-mono py-4">Sin servicios en efectivo en el rango.</div>
                        ) : (
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="bg-slate-50 border-b border-slate-200">
                                        <th className="text-left py-2 px-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Cliente</th>
                                        <th className="text-right py-2 px-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Servicios</th>
                                        <th className="text-right py-2 px-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Monto</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cashReport.top_cash_clients.map((c, i) => (
                                        <tr key={(c.client_id || "_p_") + i} className="border-b border-slate-100">
                                            <td className="py-1.5 px-2">{c.client_name}</td>
                                            <td className="py-1.5 px-2 text-right font-mono">{c.count}</td>
                                            <td className="py-1.5 px-2 text-right font-mono tabular-nums text-amber-700">{money(c.total)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
            )}
        </div>
    );
}
